// Change reps on a closed sale (owner go 24 Oct 2026): pure helpers.
import { formatPrice } from 'utils/helpers';
import { Roles, Commission, CheckoutType, BackOfficeRoles } from 'types';
import {
  RepChangeLine,
  EventCheckout,
  RepChangePreviewRep,
} from 'store/api/event';

/** Owner, 24 Oct 2026: exactly these two roles (the server checks too). */
export const changeRepsRoles = [
  Roles.COMPANY_OWNER,
  BackOfficeRoles.ADMINISTRATIVE,
];

export const REASON_MIN = 3;
export const REASON_MAX = 500;

/**
 * Why the reps of this sale cannot be changed (first version), or undefined.
 * Mirrors the server (it refuses the same sales).
 */
export const getBlockedReason = (checkout?: EventCheckout) => {
  if (!checkout) {
    return undefined;
  }
  if (checkout.chargeback?.id || checkout.lostCommissions?.length) {
    return 'This sale has a chargeback, so its reps cannot be changed.';
  }
  if (checkout.isVoided) {
    return 'This sale was voided, so its reps cannot be changed.';
  }
  if (checkout.exchanges?.length) {
    return 'This sale was exchanged, so its reps cannot be changed.';
  }
  if (checkout.exchangeCheckoutId || checkout.type === CheckoutType.Exchange) {
    return 'This is an exchange sale, so its reps cannot be changed.';
  }
  return undefined;
};

export const sharesTotal = (commissions: Commission[]) =>
  commissions.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

/** True when the picked split differs from the sale's current one. */
export const isSplitChanged = (
  current: { share: number; userId: number }[],
  picked: Commission[]
) =>
  current.length !== picked.length ||
  picked.some(
    (item) =>
      !current.some(
        (rep) =>
          rep.userId === Number(item.userId) &&
          Math.abs(rep.share - Number(item.amount)) < 0.001
      )
  );

/** What the review step says about the pick, or undefined when it can go on. */
export const getPickError = ({
  reason,
  picked,
  current,
}: {
  reason: string;
  picked: Commission[];
  current: { share: number; userId: number }[];
}) => {
  if (!picked.length) {
    return 'Pick at least one sales rep.';
  }
  const total = sharesTotal(picked);
  if (Math.abs(total - 100) > 0.001) {
    return `The shares must add up to 100% (now ${Number(total.toFixed(2))}%).`;
  }
  if (!isSplitChanged(current, picked)) {
    return 'Nothing changed yet: pick other reps or another split.';
  }
  if (reason.trim().length < REASON_MIN) {
    return 'Write the reason for the change.';
  }
  return undefined;
};

export const formatRate = (rate: number | null | undefined) =>
  rate === null || rate === undefined ? '—' : `${Number(rate)}%`;

/** "15%", or "14% → 15%" when the day's rate changes. */
export const formatRateChange = (rep: RepChangePreviewRep) => {
  if (rep.status === 'removed') {
    return formatRate(rep.before.rate);
  }
  if (rep.status === 'added') {
    return rep.before.rate !== null &&
      rep.after.rate !== null &&
      rep.before.rate !== rep.after.rate
      ? `${formatRate(rep.before.rate)} → ${formatRate(rep.after.rate)}`
      : formatRate(rep.after.rate);
  }
  return rep.before.rate !== rep.after.rate
    ? `${formatRate(rep.before.rate)} → ${formatRate(rep.after.rate)}`
    : formatRate(rep.after.rate);
};

export const formatSignedMoney = (amount: number) =>
  `${amount < 0 ? '−' : amount > 0 ? '+' : ''}${formatPrice(Math.abs(amount))}`;

/** "Keith 50% + Juana 50%" */
export const formatSplit = (reps: { name: string; share: number }[]) =>
  reps.map((rep) => `${rep.name} ${Number(rep.share)}%`).join(' + ') || '—';

/** Under each rep in the picker: "rate 10% · share was 100%" / "… · not on this sale". */
export const sellerNote = (
  rate: number | null | undefined,
  share: number | null | undefined
) =>
  `rate ${formatRate(rate)} · ${
    Number(share) > 0 ? `share was ${Number(share)}%` : 'not on this sale'
  }`;

// One pay kind: [the type and label shown when it adds, when it takes back].
const NET_KINDS: [string, string, string, string][] = [
  ['Earnings', 'Commission', 'Negative Earnings', 'Commission take-back'],
  ['Tip', 'Tip share', 'Negative Tip', 'Tip share take-back'],
  ['Adjustment', 'Level adjustment', 'Negative Adjustment', 'Level adjustment'],
  [
    'Free Shipping',
    'Free shipping share',
    'Free Shipping Refund',
    'Free shipping share back',
  ],
];

/**
 * Dev check 27 Oct (H1): the review shows one net line per rep per pay kind
 * (a move writes a line per Tip line and mirrored pairs, e.g. "Tip share
 * +15.61" under the rep who loses the tip). The net is the sum of the
 * planned lines, to the cent; a kind that nets to $0 is left out. The lines
 * written are unchanged.
 */
export const netPreviewLines = (lines: RepChangeLine[]): RepChangeLine[] => {
  const byKind = new Map<string, { cents: number; kind?: string[] }>();
  lines.forEach((line) => {
    const kind = NET_KINDS.find(
      ([plus, , minus]) => line.type === plus || line.type === minus
    );
    const key = kind ? kind[0] : line.type;
    const entry = byKind.get(key) ?? { kind, cents: 0 };
    entry.cents += Math.round((Number(line.amount) || 0) * 100);
    if (!kind && !byKind.has(key)) {
      // An unknown type keeps its own label.
      entry.kind = [line.type, line.label, line.type, line.label];
    }
    byKind.set(key, entry);
  });
  return Array.from(byKind.values())
    .filter((entry) => entry.cents !== 0)
    .map(({ cents, kind = [] }) => ({
      amount: cents / 100,
      type: cents > 0 ? kind[0] : kind[2],
      label: cents > 0 ? kind[1] : kind[3],
    }));
};
